import { Session, GroupedSession } from '../types/communication.types'

/**
 * Agrupa sessões duplicadas por guestName + room + hotelId
 * 
 * Esta função resolve o problema das sessões duplicadas (ex: 40 sessões para João Silva)
 * agrupando-as por hóspede + quarto ao invés de sessionKey (que pode ser único por sessão)
 */
export const groupSessionsByKey = (sessions: Session[]): GroupedSession[] => {
  const grouped = new Map<string, GroupedSession>()
  
  // 🔔 LOG: Verificar se as sessões têm unreadCount
  console.log('🔍 [groupSessionsByKey] Sessões recebidas:', sessions.length)
  const sessionsWithUnread = sessions.filter(s => s.unreadCount && s.unreadCount > 0)
  if (sessionsWithUnread.length > 0) {
    console.log(`🔔 [groupSessionsByKey] ${sessionsWithUnread.length} sessões com unreadCount:`, 
      sessionsWithUnread.map(s => ({ name: s.guestName, count: s.unreadCount }))
    )
  }
  
  sessions.forEach(session => {
    // Chave única: guestName + room + hotelId (mais confiável que sessionKey)
    const key = `${(session.guestName || '').trim()}-${(session.room || '').trim()}-${session.hotelId}`
    
    if (!grouped.has(key)) {
      // Primeira sessão com esta chave - criar entrada
      grouped.set(key, {
        id: session.id, // 🛠️ CORREÇÃO: Preservar ID original
        sessionKey: session.sessionKey,
        hotelId: session.hotelId,
        guestName: session.guestName,
        room: session.room,
        reservationId: session.reservationId,
        cpfMasked: session.cpfMasked,
        department: session.department,
        lang: session.lang,
        connected: session.connected,
        lastMessageAt: session.lastMessageAt,
        totalUnreadCount: session.unreadCount || 0,
        lastMessage: session.lastMessage,
        sessionCount: 1
      })
    } else {
      // Sessão duplicada - consolidar informações
      const existing = grouped.get(key)!
      
      // Somar contadores não lidos
      existing.totalUnreadCount += (session.unreadCount || 0)
      
      // Manter status conectado se pelo menos uma estiver conectada
      existing.connected = existing.connected || session.connected
      
      // Incrementar contador de sessões (para debug)
      existing.sessionCount += 1
      
      // Manter a mensagem e sessionKey mais recente
      if (!existing.lastMessageAt || 
          new Date(session.lastMessageAt) > new Date(existing.lastMessageAt)) {
        existing.lastMessage = session.lastMessage
        existing.lastMessageAt = session.lastMessageAt
        existing.sessionKey = session.sessionKey // Usar sessionKey mais recente
      }
    }
  })
  
  const result = Array.from(grouped.values())
  
  // Log para debug de duplicatas
  const duplicates = result.filter(g => g.sessionCount > 1)
  if (duplicates.length > 0) {
    console.warn('⚠️ Sessões duplicadas encontradas:', duplicates.map(d => ({
      guestName: d.guestName,
      sessionKey: d.sessionKey,
      duplicateCount: d.sessionCount
    })))
  }
  
  return result
}

/**
 * Função de agrupamento alternativo mais agressivo
 * 
 * Agrupa por múltiplos critérios para garantir consolidação
 */
export const groupSessionsAggressively = (sessions: Session[]): GroupedSession[] => {
  const grouped = new Map<string, GroupedSession>()
  
  sessions.forEach(session => {
    // Normalizar dados para comparação
    const normalizedName = (session.guestName || '').trim().toLowerCase()
    const normalizedRoom = (session.room || '').trim()
    
    // Tentar múltiplas chaves até encontrar match
    const possibleKeys = [
      `${normalizedName}-${normalizedRoom}-${session.hotelId}`, // Principal
      `${normalizedName}-${session.hotelId}`, // Por nome se quarto não bater
      `${session.sessionKey}-${session.hotelId}` // Fallback original
    ]
    
    let foundExisting = false
    let keyToUse = possibleKeys[0] // Default
    
    // Verificar se já existe entrada para este hóspede
    Array.from(grouped.entries()).forEach(([existingKey, existingSession]) => {
      const existingNormalizedName = (existingSession.guestName || '').trim().toLowerCase()
      const existingNormalizedRoom = (existingSession.room || '').trim()
      
      // Se mesmo nome e mesmo quarto, é duplicata
      if (existingNormalizedName === normalizedName && 
          existingNormalizedRoom === normalizedRoom) {
        keyToUse = existingKey
        foundExisting = true
      }
    })
    
    if (!foundExisting) {
      // Primeira sessão com esta chave - criar entrada
      grouped.set(keyToUse, {
        id: session.id, // 🛠️ CORREÇÃO: Preservar ID original da primeira sessão
        sessionKey: session.sessionKey,
        hotelId: session.hotelId,
        guestName: session.guestName,
        room: session.room,
        reservationId: session.reservationId,
        cpfMasked: session.cpfMasked,
        department: session.department,
        lang: session.lang,
        connected: session.connected,
        lastMessageAt: session.lastMessageAt,
        totalUnreadCount: session.unreadCount || 0,
        lastMessage: session.lastMessage,
        sessionCount: 1,
        conciergeMode: session.conciergeMode // 🤖 PRESERVAR conciergeMode
      })
    } else {
      // Sessão duplicada - consolidar informações
      const existing = grouped.get(keyToUse)!
      
      // Somar contadores não lidos
      existing.totalUnreadCount += (session.unreadCount || 0)
      
      // Manter status conectado se pelo menos uma estiver conectada
      existing.connected = existing.connected || session.connected
      
      // 🤖 Atualizar conciergeMode: SEMPRE usar o valor não-undefined mais recente
      // Prioridade: valor definido > undefined (nunca sobrescrever com undefined)
      if (session.conciergeMode !== undefined) {
        // Se a sessão atual tem um modo definido, sempre usar ele (é mais recente)
        existing.conciergeMode = session.conciergeMode
        console.log(`🔄 [groupSessionsAggressively] Atualizando conciergeMode: ${session.guestName} -> ${session.conciergeMode}`)
      }
      
      // Incrementar contador de sessões (para debug)
      existing.sessionCount += 1
      
      // Manter a mensagem e sessionKey mais recente
      if (!existing.lastMessageAt || 
          new Date(session.lastMessageAt) > new Date(existing.lastMessageAt)) {
        existing.lastMessage = session.lastMessage
        existing.lastMessageAt = session.lastMessageAt
        existing.sessionKey = session.sessionKey // Usar sessionKey mais recente
        
        // 🔧 IMPORTANTE: Ao atualizar para sessão mais recente, também atualizar o conciergeMode
        if (session.conciergeMode !== undefined) {
          existing.conciergeMode = session.conciergeMode
          console.log(`🔄 [groupSessionsAggressively] Modo da sessão mais recente: ${session.guestName} -> ${session.conciergeMode}`)
        }
      }
    }
  })
  
  const result = Array.from(grouped.values())
  
  // Log para debug de duplicatas
  const duplicates = result.filter(g => g.sessionCount > 1)
  if (duplicates.length > 0) {
    console.warn('⚠️ Sessões duplicadas consolidadas:', duplicates.map(d => ({
      guestName: d.guestName,
      sessionKey: d.sessionKey,
      duplicateCount: d.sessionCount
    })))
  }
  
  return result
}

/**
 * Converte GroupedSession de volta para Session (para compatibilidade)
 */
export const groupedSessionToSession = (groupedSession: GroupedSession): Session => {
  // 🔍 DEBUG: Verificar se GroupedSession tem um ID válido preservado
  console.log('🔍 DEBUG groupedSessionToSession:', {
    id: groupedSession.id,
    sessionKey: groupedSession.sessionKey,
    guestName: groupedSession.guestName,
    totalUnreadCount: groupedSession.totalUnreadCount // 🔔 ADICIONADO
  })
  
  if (groupedSession.id === 0) {
    console.warn('⚠️ SESSION ID = 0! Sessão não terá ID válido para backend:', groupedSession.sessionKey)
  }
  
  const session: Session = {
    id: groupedSession.id, // 🛠️ CORREÇÃO: Usar ID preservado da sessão original
    sessionKey: groupedSession.sessionKey,
    hotelId: groupedSession.hotelId,
    guestName: groupedSession.guestName,
    room: groupedSession.room,
    reservationId: groupedSession.reservationId,
    cpfMasked: groupedSession.cpfMasked,
    department: groupedSession.department,
    lang: groupedSession.lang,
    connected: groupedSession.connected,
    lastMessageAt: groupedSession.lastMessageAt,
    unreadCount: groupedSession.totalUnreadCount,
    lastMessage: groupedSession.lastMessage,
    conciergeMode: groupedSession.conciergeMode // 🤖 PRESERVAR conciergeMode
  }
  
  // 🔔 LOG para debug de unreadCount
  if (session.unreadCount !== undefined && session.unreadCount > 0) {
    console.log(`✅ Session com unreadCount: ${session.guestName} - Count: ${session.unreadCount}`)
  }
  
  return session
}

/**
 * Filtra mensagens por sessionKey específico
 * 
 * Esta função garante que apenas mensagens da sessão atual sejam exibidas
 */
export const filterMessagesBySession = (
  message: any, 
  targetSessionKey: string, 
  targetHotelId: number
): boolean => {
  // Verificar se a mensagem pertence à sessão específica
  return message.sessionKey === targetSessionKey && 
         message.hotelId === targetHotelId
}

/**
 * Ordena sessões por prioridade
 */
export const sortSessionsByPriority = (a: GroupedSession, b: GroupedSession): number => {
  // Prioridade 1: Sessões com mensagens não lidas
  if (a.totalUnreadCount > 0 && b.totalUnreadCount === 0) return -1
  if (a.totalUnreadCount === 0 && b.totalUnreadCount > 0) return 1

  // Prioridade 2: Sessões conectadas
  if (a.connected && !b.connected) return -1
  if (!a.connected && b.connected) return 1

  // Prioridade 3: Última mensagem mais recente
  const aTime = new Date(a.lastMessageAt).getTime()
  const bTime = new Date(b.lastMessageAt).getTime()
  return bTime - aTime
}

/**
 * Função de debug para inspecionar sessões
 */
export const debugSessions = (sessions: Session[], grouped: GroupedSession[]) => {
  console.group('🔍 Debug de Sessões')
  console.log('Sessões originais:', sessions.length)
  console.log('Sessões agrupadas:', grouped.length)
  
  // Debug: Mostrar primeiras 5 sessões para analisar padrão
  console.log('📋 Primeiras 5 sessões (para debug):')
  sessions.slice(0, 5).forEach((session, index) => {
    console.log(`${index + 1}. "${session.guestName}" - SessionKey: "${session.sessionKey}" - Room: ${session.room}`)
  })
  
  // Debug: Agrupar por nome do hóspede para ver duplicatas
  const byGuestName = sessions.reduce((acc: Record<string, Session[]>, session) => {
    if (!acc[session.guestName]) {
      acc[session.guestName] = []
    }
    acc[session.guestName].push(session)
    return acc
  }, {})
  
  console.log('👥 Sessões por hóspede:')
  Object.entries(byGuestName).forEach(([guestName, guestSessions]) => {
    if (guestSessions.length > 1) {
      console.warn(`⚠️ ${guestName}: ${guestSessions.length} sessões com sessionKeys diferentes:`)
      guestSessions.forEach((s, i) => {
        console.log(`   ${i + 1}. SessionKey: "${s.sessionKey}" - Room: ${s.room}`)
      })
    } else {
      console.log(`✅ ${guestName}: 1 sessão única`)
    }
  })
  
  const duplicatesInfo = grouped
    .filter(g => g.sessionCount > 1)
    .map(g => ({
      guestName: g.guestName,
      room: g.room,
      sessionKey: g.sessionKey,
      duplicateCount: g.sessionCount,
      totalUnread: g.totalUnreadCount
    }))
  
  if (duplicatesInfo.length > 0) {
    console.warn('Duplicatas encontradas:', duplicatesInfo)
  } else {
    console.log('⚠️ Nenhuma duplicata encontrada (possível problema: sessionKeys únicos para mesmo hóspede)')
  }
  
  console.groupEnd()
}